import React, { useMemo, useState, useRef, useEffect } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../store';
import { UserRole } from '../../types';
import { useTheme } from '../../context/ThemeContext';
import ThemeToggle from '../../components/ThemeToggle';

interface NavLinkItem {
  to: string;
  label: string;
  icon: string;
  minRole?: UserRole;
  end?: boolean;
}

const ROLE_LEVEL: Record<string, number> = {
  SUPER_ADMIN: 3,
  GROUP_ADMIN: 2,
  TENANT_ADMIN: 2,
  SALES_REP: 1,
};

const tenantLinks: NavLinkItem[] = [
  { to: '/dashboard', icon: 'dashboard', label: 'Overview' },
  { to: '/videos', icon: 'play_circle', label: 'Library' },
  { to: '/coaching', icon: 'school', label: 'Coaching' },
  { to: '/analytics', icon: 'insights', label: 'Performance' },
  { to: '/team', icon: 'groups', label: 'Team', minRole: UserRole.TENANT_ADMIN },
  { to: '/settings', icon: 'tune', label: 'Settings', minRole: UserRole.TENANT_ADMIN },
];

const superAdminLinks: NavLinkItem[] = [
  { to: '/tenants', icon: 'business', label: 'Accounts' },
  { to: '/dealer-groups', icon: 'groups', label: 'Dealer Groups' },
  { to: '/benchmarks', icon: 'leaderboard', label: 'Benchmarks' },
  { to: '/platform-settings', icon: 'key', label: 'Settings' },
];

const groupAdminLinks: NavLinkItem[] = [
  { to: '/group-admin', icon: 'corporate_fare', label: 'Overview', end: true },
  { to: '/group-admin/tenants', icon: 'storefront', label: 'Dealerships' },
  { to: '/group-admin/videos', icon: 'play_circle', label: 'Videos' },
  { to: '/group-admin/compliance', icon: 'gavel', label: 'Compliance' },
  { to: '/group-admin/benchmarks', icon: 'leaderboard', label: 'Performance' },
  { to: '/group-admin/team', icon: 'manage_accounts', label: 'Team' },
];

const TopNav: React.FC = () => {
  const navigate = useNavigate();
  const clearAuth = useAuthStore((s) => s.clearAuth);
  const user = useAuthStore((s) => s.user);
  const userRole = user?.role;
  const { theme, toggleTheme } = useTheme();

  const isSuperAdmin = userRole === UserRole.SUPER_ADMIN;
  const isGroupAdmin = userRole === UserRole.GROUP_ADMIN;
  const isSalesRep = userRole === UserRole.SALES_REP;

  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const navLinks = useMemo(() => {
    if (isSuperAdmin) return superAdminLinks;
    if (isGroupAdmin) return groupAdminLinks;
    const level = ROLE_LEVEL[userRole ?? ''] ?? 0;
    return tenantLinks.filter(
      (link) => !link.minRole || level >= (ROLE_LEVEL[link.minRole] ?? 0),
    );
  }, [userRole, isSuperAdmin, isGroupAdmin]);

  // Click-away listener
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };
    if (isDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isDropdownOpen]);

  const [isVisible, setIsVisible] = React.useState(true);
  const [isScrolled, setIsScrolled] = React.useState(false);
  const [lastScrollY, setLastScrollY] = React.useState(0);

  React.useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      
      // Toggle background
      if (currentScrollY > 20) {
        setIsScrolled(true);
      } else {
        setIsScrolled(false);
      }

      // Hide/Show logic
      if (currentScrollY > lastScrollY && currentScrollY > 100) {
        setIsVisible(false);
      } else {
        setIsVisible(true);
      }
      setLastScrollY(currentScrollY);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [lastScrollY]);

  const handleLogout = () => {
    clearAuth();
    localStorage.removeItem('vqs_tenant_id');
    navigate('/login');
  };

  return (
    <>
      {/* Background Brand Anchor (Sticky) */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden select-none z-0">
        <div className="absolute top-0 right-0 h-full hidden xl:flex items-center pr-16 opacity-[0.03] dark:opacity-[0.05]">
          <h1 className="text-[min(12vw,14vh)] font-black text-on-surface uppercase tracking-tighter transform rotate-[-90deg] origin-right leading-none whitespace-nowrap translate-y-[20%] select-none">
            SALES
          </h1>
        </div>
      </div>

      {/* Decorative Glow (Glassy Effect) */}
      <div
        className="fixed pointer-events-none opacity-40 z-0"
        style={{
          top: '-14%',
          left: '-28%',
          width: '80%',
          height: '26%',
          background: 'radial-gradient(circle at top left, var(--primary), transparent 60%)',
          filter: 'blur(80px)',
        }}
      />

      <header className={`sticky top-0 z-[100] w-full transition-all duration-300 ${isVisible ? 'translate-y-0' : '-translate-y-full'} ${isScrolled ? 'bg-white/80 dark:bg-slate-950/80 backdrop-blur-lg border-b border-slate-100 dark:border-white/5 shadow-sm' : 'bg-transparent'}`}>
        <div className="mx-auto flex h-20 w-full max-w-7xl items-center justify-between px-6">
          {/* Left: Logo */}
          <div className="flex items-center">
            <div className="flex flex-col gap-1 cursor-pointer" onClick={() => navigate('/')}>
              <img 
                src={theme === 'dark' ? '/truvideo-logo.png' : '/truvideo-logo-dark.png'} 
                alt="TruVideo" 
                className="h-7 w-auto transition-all duration-300" 
              />
              <div className="flex items-center justify-end gap-2">
                <span className="font-headline text-[10px] font-bold tracking-[0.2em] text-on-surface leading-none">VQS SALES</span>
                {isSuperAdmin && (
                  <span className="text-[8px] font-label tracking-[0.1em] uppercase text-primary font-bold">
                    Admin
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Center: Nav */}
          <nav className="hidden lg:flex flex-1 justify-center items-center gap-10">
            {navLinks.map((link) => (
              <NavLink
                key={link.to}
                to={link.to}
                end={link.end}
                className={({ isActive }) =>
                  `flex items-center gap-2 py-2 font-label text-[11px] tracking-[0.15em] uppercase transition-all duration-200 border-b-2 ${
                    isActive
                      ? 'text-primary border-primary font-bold'
                      : 'text-on-surface/50 border-transparent hover:text-on-surface hover:border-on-surface/20'
                  }`
                }
              >
                <span className="material-symbols-outlined text-[18px]">{link.icon}</span>
                {link.label}
              </NavLink>
            ))}
          </nav>

          {/* Right: Actions & User */}
          <div className="flex items-center gap-6">
            {isSalesRep && (
              <button
                onClick={() => navigate('/upload')}
                className="hidden xl:flex items-center gap-2 bg-primary text-on-primary px-5 py-2.5 rounded-xl font-label text-[10px] tracking-[0.15em] uppercase font-bold hover:brightness-110 active:scale-[0.98] transition-all shadow-md shadow-primary/20"
              >
                <span className="material-symbols-outlined text-[18px]">upload</span>
                Upload
              </button>
            )}

            {/* Theme Toggle moved to dropdown below */}

            <div className="relative" ref={dropdownRef}>
              <button 
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                className="flex items-center gap-3 transition-all outline-none"
              >
                <div className={`relative h-10 w-10 overflow-hidden rounded-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center border-2 shadow-sm transition-all duration-300 ${isDropdownOpen ? 'border-primary ring-4 ring-primary/10' : 'border-white dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600'}`}>
                  <span className="text-sm font-bold text-slate-600 dark:text-slate-300">
                    {user?.firstName?.charAt(0).toUpperCase() || 'U'}
                  </span>
                  <div className={`absolute inset-0 bg-primary/5 transition-opacity ${isDropdownOpen ? 'opacity-100' : 'opacity-0'}`} />
                </div>
                
                <div className="hidden sm:flex flex-col items-start leading-tight text-left">
                  <span className={`text-sm font-bold transition-colors ${isDropdownOpen ? 'text-primary' : 'text-slate-900 dark:text-white'}`}>
                    {user?.firstName} {user?.lastName}
                  </span>
                  <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">
                    {({ SUPER_ADMIN: 'Super Admin', GROUP_ADMIN: 'Group Admin', TENANT_ADMIN: 'Account Admin', SALES_REP: 'Sales Rep' } as Record<string, string>)[userRole ?? ''] ?? userRole?.replace('_', ' ')}
                  </span>
                </div>
              </button>
              
              {/* Redesigned Dropdown (Turbo Style) */}
              <div className={`absolute right-0 top-full mt-4 w-64 p-2 bg-white dark:bg-slate-900 border border-slate-100 dark:border-slate-800 rounded-2xl shadow-2xl transition-all z-50 animate-in fade-in zoom-in duration-200 ${isDropdownOpen ? 'opacity-100 visible translate-y-0' : 'opacity-0 invisible translate-y-2'}`}>
                <div className="px-3 py-4 mb-1">
                  <p className="text-sm font-bold text-slate-900 dark:text-white uppercase truncate">{user?.firstName} {user?.lastName}</p>
                  <p className="text-xs font-semibold text-slate-400 truncate">{user?.email}</p>
                </div>
                
                <div className="h-px bg-slate-50 dark:bg-white/5 mx-[-8px]" />
                
                <div className="py-2 space-y-1">
                  {/* Theme Switcher inside Dropdown */}
                  <div className="px-3 py-2">
                    <div className="flex items-center justify-between p-2 rounded-xl bg-slate-50 dark:bg-slate-950/50 border border-slate-100 dark:border-white/5 transition-all">
                      <div className="flex items-center gap-3">
                        <span className={`material-symbols-outlined text-[20px] ${theme === 'dark' ? 'text-yellow-400' : 'text-blue-600'}`}>
                          {theme === 'dark' ? 'light_mode' : 'dark_mode'}
                        </span>
                        <span className="text-[11px] font-bold text-slate-600 dark:text-slate-400 uppercase tracking-widest">
                          {theme === 'dark' ? 'Light Theme' : 'Dark Theme'}
                        </span>
                      </div>
                      <button 
                        onClick={toggleTheme}
                        className={`w-10 h-5 rounded-full relative transition-all duration-500 ease-in-out cursor-pointer ${theme === 'dark' ? 'bg-primary' : 'bg-slate-200 dark:bg-slate-800'}`}
                                      >
                        <div className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow-sm transition-all duration-500 ease-in-out ${theme === 'dark' ? 'right-0.5 shadow-primary/20' : 'left-0.5 shadow-slate-300'}`} />
                      </button>
                    </div>
                  </div>

                  {/* Profile Settings (only for non-admins) */}
                  {userRole !== UserRole.SUPER_ADMIN && (
                    <NavLink
                      to="/profile"
                      className="flex h-11 items-center gap-3 px-3 rounded-xl text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-white/5 transition-colors group outline-none"
                    >
                      <span className="material-symbols-outlined text-[20px] text-slate-400 group-hover:text-primary transition-colors">person</span>
                      <span className="text-sm font-bold">Profile Settings</span>
                      {/* <span className="ml-auto material-symbols-outlined text-[16px] text-slate-300 dark:text-slate-700 opacity-0 group-hover:opacity-100 transition-all">chevron_right</span> */}
                    </NavLink>
                  )}
                </div>
                  <button
                    onClick={handleLogout}
                    className="w-full flex h-11 items-center gap-3 px-3 rounded-xl text-error hover:bg-error/5 cursor-pointer transition-colors mt-1 outline-none"
                  >
                    <span className="material-symbols-outlined text-[20px]">logout</span>
                    <span className="text-sm font-bold">Sign Out</span>
                  </button>
                </div>
              </div>
          </div>
        </div>
      </header>
    </>
  );
};

export default TopNav;
